import React from "react"
import CheckCircle from "@kiwicom/orbit-components/lib/icons/CheckCircle"
import Stack from "@kiwicom/orbit-components/lib/Stack"
import Card, { CardSection } from "@kiwicom/orbit-components/lib/Card"
import { loadSession, Test, Features } from "@kiwicom/darwin"
import Badge from "@kiwicom/orbit-components/lib/Badge"
import Text from "@kiwicom/orbit-components/lib/Text"
import Select from "@kiwicom/orbit-components/lib/Select"
import Box from "@kiwicom/orbit-components/lib/Box"
import { mockFeatureFormat, mockFormat, MockParams } from "@kiwicom/darwin/mock"
import Alert from "@kiwicom/orbit-components/lib/Alert"
import Button from "@kiwicom/orbit-components/lib/Button"

import darwinMockSuggestions from "../../../../../src/mmb/services/abTesting/darwinMockSuggestions"
import FeatureToggle from "./FeatureToggle"

type WindowDarwin = {
  tests: Test[]
  features: Features
  winners: Test[]
}

const darwinMock = {
  tests: [],
  features: {},
  winners: [],
} as const

const DarwinMockUI = () => {
  const remoteAbTests = loadSession()

  // @ts-expect-error: Flow to TS migration.
  const darwin: WindowDarwin = window.__DARWIN__ || darwinMock

  const selectedAbTests = darwin.tests
  const selectedAbTestsNames = selectedAbTests.map((test) => test.name)

  // features from remote and local config
  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
  const features: {
    [featureName: string]: boolean
  } = React.useMemo(
    () => ({
      // Fix this the next time the file is edited. (Flow to TS)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...darwinMockSuggestions.features.reduce<Record<string, any>>(
        (prev, featureName) => ({ ...prev, [featureName]: false }),
        {},
      ),
      ...darwin.features,
    }),
    [darwin.features],
  )

  const onAbTestOverride = React.useCallback((name: string, value: string) => {
    window.location.replace(mockFormat([{ name, value }]))
  }, [])

  const onFeatureOverride = React.useCallback(
    (featureName: string, newValue: boolean) => {
      const featuresToOverwrite = { ...features, [featureName]: newValue } as const
      window.location.replace(mockFeatureFormat(featuresToOverwrite))
    },
    [features],
  )

  const clearFeatureOverrides = React.useCallback(() => {
    return window.location.replace(mockFeatureFormat({}))
  }, [])

  // tests from local config only
  const testSuggestions: {
    name: string
    values: string[]
  }[] = React.useMemo(
    () =>
      darwinMockSuggestions.tests.sort((a, b) =>
        selectedAbTestsNames.includes(a.name) && !selectedAbTestsNames.includes(b.name) ? -1 : 1,
      ),
    [selectedAbTestsNames],
  )

  const searchParam = new URLSearchParams(window.location.search)
  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const isMockedTest = searchParam?.get(MockParams.MOCK_TEST)
  const isMockedFeature =
    // Fix this the next time the file is edited. (Flow to TS)
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/prefer-nullish-coalescing
    searchParam?.get(MockParams.MOCK_FEATURE_ON) || searchParam?.get(MockParams.MOCK_FEATURE_OFF)

  return (
    <div>
      <Stack>
        {isMockedTest && (
          <Alert type="warning" title="Remote AB test is overridden.">
            <Stack>
              <span>
                Remote configuration is not applied, because is overwritten by this debug modal
              </span>
              <Stack direction="row" align="center" justify="start">
                <Text>Remote configuration: </Text>
                <strong>
                  {Object.keys(remoteAbTests).map((abTestName) => (
                    <pre key={abTestName}>
                      {abTestName} = {remoteAbTests[abTestName]}
                    </pre>
                  ))}
                </strong>
              </Stack>
            </Stack>
          </Alert>
        )}

        <Card
          title="AB test"
          titleAs="h2"
          description={
            selectedAbTests.length > 0 ? (
              <>
                {selectedAbTests.map((selectedAbTest) => (
                  <Alert type="success" key={selectedAbTest.name}>
                    <Stack
                      direction="row"
                      align="center"
                      justify="between"
                      key={selectedAbTest.name}
                    >
                      <Text type="primary" size="normal" weight="bold">
                        {selectedAbTest.name}
                      </Text>
                      <Badge icon={<CheckCircle ariaHidden />} type="success">
                        {selectedAbTest.value}
                      </Badge>
                    </Stack>
                  </Alert>
                ))}
              </>
            ) : (
              "No active AB test."
            )
          }
        >
          <CardSection>
            {testSuggestions.map((suggestion) => (
              <Stack
                key={suggestion.name}
                direction="row"
                align="center"
                justify="between"
                spaceAfter="small"
                spacing="none"
              >
                <Text
                  type={selectedAbTestsNames.includes(suggestion.name) ? "success" : "primary"}
                  weight={selectedAbTestsNames.includes(suggestion.name) ? "bold" : "normal"}
                >
                  {suggestion.name}
                </Text>
                <Box width="auto">
                  <Select
                    // @ts-expect-error: Flow to TS migration.
                    value={
                      selectedAbTestsNames.includes(suggestion.name)
                        ? selectedAbTests.filter((test) => test.name === suggestion.name)[0].value
                        : null
                    }
                    placeholder="Value"
                    options={suggestion.values.map((abvalue) => ({
                      label: abvalue,
                      value: abvalue,
                    }))}
                    onChange={(event) =>
                      onAbTestOverride(suggestion.name, event.currentTarget.value)
                    }
                  />
                </Box>
              </Stack>
            ))}
          </CardSection>
        </Card>

        <Card
          title="Feature toggling"
          titleAs="h2"
          actions={
            isMockedFeature && (
              <Button onClick={clearFeatureOverrides} type="criticalSubtle">
                Clear overrides
              </Button>
            )
          }
        >
          <CardSection>
            {Object.keys(features).map((feature) => (
              <FeatureToggle
                key={feature}
                feature={feature}
                onFeatureOverride={onFeatureOverride}
              />
            ))}
          </CardSection>
        </Card>

        <Text type="secondary">
          To add AB test or feature toggle here, check file{" "}
          <pre>src/mmb/services/abTesting/darwinMockSuggestions.js</pre>
        </Text>
      </Stack>
    </div>
  )
}

export default DarwinMockUI
